import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  createFeedbackFlowQuestion as createFeedbackFlowQuestionRequest,
  deleteFeedbackFlowQuestion,
  getFeedbackFlowErrorMessage,
  patchFeedbackFlowIntro,
  patchFeedbackFlowOutro,
  patchFeedbackFlowSections,
  reorderFeedbackFlowQuestions as reorderFeedbackFlowQuestionsRequest,
  updateFeedbackFlowQuestion,
  useFeedbackFlow,
} from "../api/feedbackFlowApi";
import { useExperimentStore } from "../store/useExperimentStore";
import {
  createDefaultDemographicsQuestions,
  createDefaultPersonalInfoQuestions,
  createFeedbackFlowQuestion,
  getFeedbackFlowSectionEnabled,
  getNextSortOrderForSection,
  getQuestionsBySection,
  hydrateFeedbackFlow,
  isDraftFeedbackQuestionId,
  removeFeedbackFlowQuestion,
  reorderFeedbackFlowSectionQuestions,
  replaceFeedbackFlowQuestionId,
  replaceFeedbackFlowSectionQuestions,
  setFeedbackFlowSectionEnabled,
  upsertFeedbackFlowQuestion,
} from "../utils/feedbackFlow";

import type {
  ExperimentFormData,
  FeedbackFlow,
  FeedbackFlowContent,
  FeedbackFlowQuestion,
  FeedbackFlowQuestionSection,
  TaggedTarget,
} from "../types/experiment.types";

type TaggingSection = Extract<
  FeedbackFlowQuestionSection,
  "demographics" | "personal_info"
>;

interface UseTaggingFeedbackFlowParams {
  experimentData: ExperimentFormData;
  setExperimentData: React.Dispatch<React.SetStateAction<ExperimentFormData>>;
  experimentId?: string;
  iterationId?: string | null;
}

const CONTENT_SYNC_DEBOUNCE_MS = 700;
const PRODUCT_QUESTION_PREMISE_ERROR =
  "Choose a core research question before saving this product question.";

const buildSectionsRequest = (
  feedbackFlow: FeedbackFlow,
  section: TaggingSection,
  enabled: boolean,
) => ({
  demographicsEnabled:
    section === "demographics" ? enabled : feedbackFlow.demographicsEnabled,
  personalInfoEnabled:
    section === "personal_info" ? enabled : feedbackFlow.personalInfoEnabled,
});

const serializeContent = (content: FeedbackFlowContent) =>
  JSON.stringify({
    enabled: content.enabled,
    description: content.description,
    terms: content.terms ?? "",
  });

const getDefaultQuestionsForSection = (
  section: TaggingSection,
): FeedbackFlowQuestion[] =>
  section === "demographics"
    ? createDefaultDemographicsQuestions()
    : createDefaultPersonalInfoQuestions();

const isProductQuestionMissingPremise = (
  question: FeedbackFlowQuestion,
): boolean =>
  question.section === "prototype" && !question.premiseId?.trim();

export const useTaggingFeedbackFlow = ({
  experimentData,
  setExperimentData,
  experimentId,
  iterationId,
}: UseTaggingFeedbackFlowParams) => {
  const setPreviewFeedbackFlow = useExperimentStore(
    (state) => state.setPreviewFeedbackFlow,
  );
  const persistenceKey = useMemo(
    () => `${experimentId ?? "local"}:${iterationId ?? "local"}`,
    [experimentId, iterationId],
  );
  const canPersistToApi = Boolean(experimentId && iterationId);
  const feedbackFlowQuery = useFeedbackFlow(experimentId, iterationId, {
    enabled: canPersistToApi,
  });
  const [feedbackFlowDraft, setFeedbackFlowDraft] = useState<FeedbackFlow>(() =>
    hydrateFeedbackFlow(experimentData as ExperimentFormData & Record<string, unknown>),
  );
  const [resolvedBaseUrl, setResolvedBaseUrl] = useState<string | undefined>(
    undefined,
  );
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isStructureSyncing, setIsStructureSyncing] = useState(false);
  const [activeContentSyncCount, setActiveContentSyncCount] = useState(0);
  const [hasHydratedFromServer, setHasHydratedFromServer] = useState(
    !canPersistToApi,
  );
  const feedbackFlowDraftRef = useRef(feedbackFlowDraft);
  const hasHydrationPendingRef = useRef(canPersistToApi);
  const lastPersistenceKeyRef = useRef<string | null>(null);
  const lastSyncedIntroRef = useRef(serializeContent(feedbackFlowDraft.intro));
  const lastSyncedOutroRef = useRef(serializeContent(feedbackFlowDraft.outro));

  useEffect(() => {
    feedbackFlowDraftRef.current = feedbackFlowDraft;
  }, [feedbackFlowDraft]);

  useEffect(() => {
    if (lastPersistenceKeyRef.current === persistenceKey) {
      return;
    }

    const nextDraft = hydrateFeedbackFlow(
      experimentData as ExperimentFormData & Record<string, unknown>,
    );

    lastPersistenceKeyRef.current = persistenceKey;
    setFeedbackFlowDraft(nextDraft);
    setResolvedBaseUrl(undefined);
    setSyncError(null);
    lastSyncedIntroRef.current = serializeContent(nextDraft.intro);
    lastSyncedOutroRef.current = serializeContent(nextDraft.outro);
    hasHydrationPendingRef.current = canPersistToApi;
    setHasHydratedFromServer(!canPersistToApi);
  }, [canPersistToApi, experimentData, persistenceKey]);

  useEffect(() => {
    if (!feedbackFlowQuery.data) {
      return;
    }

    setResolvedBaseUrl(feedbackFlowQuery.data.baseUrl);

    if (!hasHydrationPendingRef.current) {
      return;
    }

    const nextFeedbackFlow = feedbackFlowQuery.data.feedbackFlow;
    setFeedbackFlowDraft(nextFeedbackFlow);
    feedbackFlowDraftRef.current = nextFeedbackFlow;
    lastSyncedIntroRef.current = serializeContent(nextFeedbackFlow.intro);
    lastSyncedOutroRef.current = serializeContent(nextFeedbackFlow.outro);
    hasHydrationPendingRef.current = false;
    setHasHydratedFromServer(true);
    setSyncError(null);
  }, [feedbackFlowQuery.data]);

  useEffect(() => {
    if (!canPersistToApi || !feedbackFlowQuery.isError) {
      return;
    }

    hasHydrationPendingRef.current = false;
    setHasHydratedFromServer(true);
    setSyncError(
      getFeedbackFlowErrorMessage(
        feedbackFlowQuery.error,
        "Unable to load the tagging feedback flow.",
      ),
    );
  }, [canPersistToApi, feedbackFlowQuery.error, feedbackFlowQuery.isError]);

  useEffect(() => {
    setPreviewFeedbackFlow(feedbackFlowDraft);
    setExperimentData((previous) => {
      const {
        annotations: _annotations,
        frontMatter: _frontMatter,
        endMatter: _endMatter,
        personalInfoQuestions: _personalInfoQuestions,
        demographicsQuestions: _demographicsQuestions,
        ...rest
      } = previous as ExperimentFormData & Record<string, unknown>;

      return {
        ...rest,
        feedbackFlow: feedbackFlowDraft,
      };
    });
  }, [feedbackFlowDraft, setExperimentData, setPreviewFeedbackFlow]);

  const beginContentSync = useCallback(() => {
    setActiveContentSyncCount((current) => current + 1);
  }, []);

  const endContentSync = useCallback(() => {
    setActiveContentSyncCount((current) => Math.max(0, current - 1));
  }, []);

  const refetchFeedbackFlow = useCallback(async () => {
    if (!canPersistToApi) {
      return;
    }

    hasHydrationPendingRef.current = true;
    await feedbackFlowQuery.refetch();
  }, [canPersistToApi, feedbackFlowQuery]);

  const reportSyncError = useCallback((error: unknown, fallback: string) => {
    setSyncError(getFeedbackFlowErrorMessage(error, fallback));
  }, []);

  const updateIntro = useCallback(
    (updater: (current: FeedbackFlowContent) => FeedbackFlowContent) => {
      setFeedbackFlowDraft((previous) => ({
        ...previous,
        intro: updater(previous.intro),
      }));
    },
    [],
  );

  const updateOutro = useCallback(
    (updater: (current: FeedbackFlowContent) => FeedbackFlowContent) => {
      setFeedbackFlowDraft((previous) => ({
        ...previous,
        outro: updater(previous.outro),
      }));
    },
    [],
  );

  const addQuestion = useCallback(
    (
      section: FeedbackFlowQuestionSection,
      overrides?: Partial<FeedbackFlowQuestion>,
    ) => {
      setFeedbackFlowDraft((previous) => {
        const nextQuestion = createFeedbackFlowQuestion(section, {
          sortOrder: getNextSortOrderForSection(previous, section),
          ...overrides,
        });

        return upsertFeedbackFlowQuestion(previous, nextQuestion);
      });
    },
    [],
  );

  const persistContent = useCallback(
    async (
      contentType: "intro" | "outro",
      content: FeedbackFlowContent,
      serializedContent: string,
    ) => {
      if (!experimentId || !iterationId || !hasHydratedFromServer) {
        return;
      }

      beginContentSync();
      try {
        const result =
          contentType === "intro"
            ? await patchFeedbackFlowIntro(
                experimentId,
                iterationId,
                content,
                resolvedBaseUrl,
              )
            : await patchFeedbackFlowOutro(
                experimentId,
                iterationId,
                content,
                resolvedBaseUrl,
              );

        setResolvedBaseUrl(result.baseUrl);
        if (contentType === "intro") {
          lastSyncedIntroRef.current = serializedContent;
        } else {
          lastSyncedOutroRef.current = serializedContent;
        }
        setSyncError(null);
      } catch (error) {
        reportSyncError(
          error,
          contentType === "intro"
            ? "Unable to save the intro content."
            : "Unable to save the outro content.",
        );
      } finally {
        endContentSync();
      }
    },
    [
      beginContentSync,
      endContentSync,
      experimentId,
      hasHydratedFromServer,
      iterationId,
      reportSyncError,
      resolvedBaseUrl,
    ],
  );

  useEffect(() => {
    if (!canPersistToApi || !hasHydratedFromServer) {
      return;
    }

    const serializedIntro = serializeContent(feedbackFlowDraft.intro);
    if (serializedIntro === lastSyncedIntroRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      void persistContent("intro", feedbackFlowDraft.intro, serializedIntro);
    }, CONTENT_SYNC_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    canPersistToApi,
    feedbackFlowDraft.intro,
    hasHydratedFromServer,
    persistContent,
  ]);

  useEffect(() => {
    if (!canPersistToApi || !hasHydratedFromServer) {
      return;
    }

    const serializedOutro = serializeContent(feedbackFlowDraft.outro);
    if (serializedOutro === lastSyncedOutroRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      void persistContent("outro", feedbackFlowDraft.outro, serializedOutro);
    }, CONTENT_SYNC_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    canPersistToApi,
    feedbackFlowDraft.outro,
    hasHydratedFromServer,
    persistContent,
  ]);

  const saveQuestion = useCallback(
    async (nextQuestion: FeedbackFlowQuestion): Promise<boolean> => {
      const currentQuestion = feedbackFlowDraftRef.current.questions.find(
        (question) => question.id === nextQuestion.id,
      );

      if (!currentQuestion) {
        return false;
      }

      if (isProductQuestionMissingPremise(nextQuestion)) {
        setSyncError(PRODUCT_QUESTION_PREMISE_ERROR);
        return false;
      }

      setFeedbackFlowDraft((previous) =>
        upsertFeedbackFlowQuestion(previous, nextQuestion),
      );

      if (!experimentId || !iterationId) {
        return true;
      }

      if (isDraftFeedbackQuestionId(nextQuestion.id)) {
        try {
          const result = await createFeedbackFlowQuestionRequest(
            experimentId,
            iterationId,
            nextQuestion,
            resolvedBaseUrl,
          );
          let finalQuestion = result.question;
          let nextBaseUrl = result.baseUrl;
          let reactionSaveFailed = false;

          if (nextQuestion.source === "ai_generated" && nextQuestion.sentiment) {
            try {
              const persistedReactionQuestion: FeedbackFlowQuestion = {
                ...result.question,
                sentiment: nextQuestion.sentiment,
                dislikeReason:
                  nextQuestion.sentiment === "dislike"
                    ? nextQuestion.dislikeReason ?? null
                    : null,
              };
              const updateResult = await updateFeedbackFlowQuestion(
                experimentId,
                iterationId,
                persistedReactionQuestion,
                nextBaseUrl,
              );
              nextBaseUrl = updateResult.baseUrl;
              finalQuestion = persistedReactionQuestion;
            } catch (error) {
              reactionSaveFailed = true;
              reportSyncError(
                error,
                "The question was created, but its AI feedback reaction could not be saved.",
              );
            }
          }

          setResolvedBaseUrl(nextBaseUrl);
          setFeedbackFlowDraft((previous) =>
            replaceFeedbackFlowQuestionId(previous, nextQuestion.id, finalQuestion),
          );
          if (!reactionSaveFailed) {
            setSyncError(null);
          }
          return true;
        } catch (error) {
          reportSyncError(error, "Unable to create the feedback question.");
          return false;
        }
      }

      try {
        const result = await updateFeedbackFlowQuestion(
          experimentId,
          iterationId,
          nextQuestion,
          resolvedBaseUrl,
        );
        setResolvedBaseUrl(result.baseUrl);
        setSyncError(null);
        return true;
      } catch (error) {
        setFeedbackFlowDraft((previous) =>
          upsertFeedbackFlowQuestion(previous, currentQuestion),
        );
        reportSyncError(error, "Unable to update the feedback question.");
        return false;
      }
    },
    [experimentId, iterationId, reportSyncError, resolvedBaseUrl],
  );

  const deleteQuestion = useCallback(
    async (questionId: string): Promise<boolean> => {
      const currentQuestion = feedbackFlowDraftRef.current.questions.find(
        (question) => question.id === questionId,
      );

      if (!currentQuestion) {
        return false;
      }

      if (
        !experimentId ||
        !iterationId ||
        isDraftFeedbackQuestionId(currentQuestion.id)
      ) {
        setFeedbackFlowDraft((previous) =>
          removeFeedbackFlowQuestion(previous, questionId),
        );
        return true;
      }

      try {
        const result = await deleteFeedbackFlowQuestion(
          experimentId,
          iterationId,
          currentQuestion.id,
          resolvedBaseUrl,
        );
        setResolvedBaseUrl(result.baseUrl);
        setFeedbackFlowDraft((previous) =>
          removeFeedbackFlowQuestion(previous, questionId),
        );
        setSyncError(null);
        return true;
      } catch (error) {
        reportSyncError(error, "Unable to delete the feedback question.");
        return false;
      }
    },
    [experimentId, iterationId, reportSyncError, resolvedBaseUrl],
  );

  const setSectionEnabled = useCallback(
    (section: TaggingSection, enabled: boolean) => {
      const currentDraft = feedbackFlowDraftRef.current;
      if (getFeedbackFlowSectionEnabled(currentDraft, section) === enabled) {
        return;
      }

      const sectionQuestions = getQuestionsBySection(currentDraft, section);

      if (enabled) {
        const shouldSeedQuestions = sectionQuestions.length === 0;
        const seededQuestions = shouldSeedQuestions
          ? getDefaultQuestionsForSection(section)
          : [];

        setFeedbackFlowDraft((previous) => {
          const nextDraft = setFeedbackFlowSectionEnabled(previous, section, true);

          return shouldSeedQuestions
            ? replaceFeedbackFlowSectionQuestions(nextDraft, section, seededQuestions)
            : nextDraft;
        });

        if (!experimentId || !iterationId) {
          return;
        }

        void (async () => {
          setIsStructureSyncing(true);
          try {
            const sectionResult = await patchFeedbackFlowSections(
              experimentId,
              iterationId,
              buildSectionsRequest(currentDraft, section, true),
              resolvedBaseUrl,
            );

            const persistedQuestions: FeedbackFlowQuestion[] = [];
            let nextBaseUrl = sectionResult.baseUrl;

            if (shouldSeedQuestions) {
              for (const question of seededQuestions) {
                const createResult = await createFeedbackFlowQuestionRequest(
                  experimentId,
                  iterationId,
                  question,
                  nextBaseUrl,
                );
                nextBaseUrl = createResult.baseUrl;
                persistedQuestions.push(createResult.question);
              }
            }

            setResolvedBaseUrl(nextBaseUrl);
            setFeedbackFlowDraft((previous) => {
              const nextDraft = setFeedbackFlowSectionEnabled(
                previous,
                section,
                true,
              );

              return shouldSeedQuestions
                ? seededQuestions.reduce((draft, seededQuestion, index) => {
                    const persistedQuestion = persistedQuestions[index];

                    return persistedQuestion
                      ? replaceFeedbackFlowQuestionId(
                          draft,
                          seededQuestion.id,
                          persistedQuestion,
                        )
                      : draft;
                  }, nextDraft)
                : nextDraft;
            });
            setSyncError(null);
          } catch (error) {
            reportSyncError(error, "Unable to enable this participant section.");
            await refetchFeedbackFlow();
          } finally {
            setIsStructureSyncing(false);
          }
        })();

        return;
      }

      setFeedbackFlowDraft((previous) =>
        setFeedbackFlowSectionEnabled(previous, section, false),
      );

      if (!experimentId || !iterationId) {
        return;
      }

      void (async () => {
        setIsStructureSyncing(true);
        try {
          const patchResult = await patchFeedbackFlowSections(
            experimentId,
            iterationId,
            buildSectionsRequest(currentDraft, section, false),
            resolvedBaseUrl,
          );

          setResolvedBaseUrl(patchResult.baseUrl);
          setSyncError(null);
        } catch (error) {
          reportSyncError(error, "Unable to disable this participant section.");
          await refetchFeedbackFlow();
        } finally {
          setIsStructureSyncing(false);
        }
      })();
    },
    [
      experimentId,
      iterationId,
      refetchFeedbackFlow,
      reportSyncError,
      resolvedBaseUrl,
    ],
  );

  const reorderQuestions = useCallback(
    async (
      section: FeedbackFlowQuestionSection,
      questionIds: string[],
    ): Promise<boolean> => {
      const currentDraft = feedbackFlowDraftRef.current;
      const sectionQuestions = getQuestionsBySection(currentDraft, section);
      const sectionQuestionIds = sectionQuestions.map((question) => question.id);

      if (
        sectionQuestions.length === 0 ||
        sectionQuestionIds.length !== questionIds.length ||
        sectionQuestionIds.some((questionId) => !questionIds.includes(questionId))
      ) {
        return false;
      }

      if (sectionQuestionIds.every((questionId, index) => questionId === questionIds[index])) {
        return true;
      }

      if (
        experimentId &&
        iterationId &&
        sectionQuestions.some((question) => isDraftFeedbackQuestionId(question.id))
      ) {
        setSyncError("Save all new questions in this section before rearranging them.");
        return false;
      }

      const reorderedDraft = reorderFeedbackFlowSectionQuestions(
        currentDraft,
        section,
        questionIds,
      );
      feedbackFlowDraftRef.current = reorderedDraft;
      setFeedbackFlowDraft(reorderedDraft);

      if (!experimentId || !iterationId) {
        return true;
      }

      setIsStructureSyncing(true);
      try {
        const result = await reorderFeedbackFlowQuestionsRequest(
          experimentId,
          iterationId,
          {
            section,
            questionIds,
          },
          resolvedBaseUrl,
        );
        setResolvedBaseUrl(result.baseUrl);
        setSyncError(null);
        return true;
      } catch (error) {
        reportSyncError(error, "Unable to rearrange the feedback questions.");
        await refetchFeedbackFlow();
        return false;
      } finally {
        setIsStructureSyncing(false);
      }
    },
    [
      experimentId,
      iterationId,
      refetchFeedbackFlow,
      reportSyncError,
      resolvedBaseUrl,
    ],
  );

  const applyTaggedTarget = useCallback(
    (questionId: string | null, taggedTarget: TaggedTarget) => {
      if (!questionId) {
        addQuestion("prototype", {
          taggedTarget,
        });
        return;
      }

      const currentQuestion = feedbackFlowDraftRef.current.questions.find(
        (question) => question.id === questionId,
      );

      if (!currentQuestion) {
        return;
      }

      const nextQuestion: FeedbackFlowQuestion = {
        ...currentQuestion,
        taggedTarget,
      };

      setFeedbackFlowDraft((previous) =>
        upsertFeedbackFlowQuestion(previous, nextQuestion),
      );

      if (isProductQuestionMissingPremise(nextQuestion)) {
        setSyncError(PRODUCT_QUESTION_PREMISE_ERROR);
        return;
      }

      if (
        !experimentId ||
        !iterationId ||
        isDraftFeedbackQuestionId(currentQuestion.id)
      ) {
        return;
      }

      void (async () => {
        try {
          const result = await updateFeedbackFlowQuestion(
            experimentId,
            iterationId,
            nextQuestion,
            resolvedBaseUrl,
          );
          setResolvedBaseUrl(result.baseUrl);
          setSyncError(null);
        } catch (error) {
          setFeedbackFlowDraft((previous) =>
            upsertFeedbackFlowQuestion(previous, currentQuestion),
          );
          reportSyncError(error, "Unable to save the updated tagged target.");
        }
      })();
    },
    [addQuestion, experimentId, iterationId, reportSyncError, resolvedBaseUrl],
  );

  return {
    feedbackFlowDraft,
    updateIntro,
    updateOutro,
    addQuestion,
    saveQuestion,
    deleteQuestion,
    reorderQuestions,
    setSectionEnabled,
    applyTaggedTarget,
    refetchFeedbackFlow,
    dismissSyncError: () => setSyncError(null),
    syncError,
    isFeedbackFlowInitializing: canPersistToApi && !hasHydratedFromServer,
    isFeedbackFlowLoading: feedbackFlowQuery.isLoading,
    isFeedbackFlowSyncing: activeContentSyncCount > 0 || isStructureSyncing,
    canPersistToApi,
  };
};
